import './search-result.scss'

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Pagination } from '@entur/menu'
import { Heading2 } from '@entur/typography'
import { Table, TableHead, TableRow, TableBody, HeaderCell, DataCell } from '@entur/table'
import { GridContainer, GridItem } from '@entur/grid'
import { BannerAlertBox } from '@entur/alert'
import { TertiaryButton } from '@entur/button'
import { FileIcon } from '@entur/icons'
import { space } from '@entur/tokens'
import { BulletBadge } from '@entur/layout'
import { Page, TicketRightHolder, Fetch, FetchState } from '../../api-clients/api-types'
import { toPrintFormat } from '../../utils/date-utils'
import { NoResult } from '../no-result/no-result'
import SearchResultLoader from './search-result-loader'
import noResultImg from './no-results.jpg'
import emptyResultImg from './empty-results.jpg'
import { SearchHistory } from '../../types'
import { DraftValue } from '../draft-value/draft-value'

type TicketHolderListProps = {
  searchState: SearchHistory
}
const TicketHolderList: React.FC<TicketHolderListProps> = (props) => {
  const navigate = useNavigate()
  const navigateTo = (id: string, _items: TicketRightHolder[]) => {
    const t = id.split('-')
    const employeeId = t[0] === 'Employee' ? t[1] : t[2] // TODO: Replace with employeeId form ticket right holder when avaiable 21.07.2020
    navigate(`/ansatt/${employeeId}${t[0] === 'Employee' ? '' : '#familiemedlemmer'}`, {
      state: {
        search: props.searchState,
      },
    })
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <HeaderCell style={{ width: '40rem' }}>Navn</HeaderCell>
          <HeaderCell>Fødselsdato</HeaderCell>
          <HeaderCell>Selskap</HeaderCell>
          <HeaderCell>Ansattnummer</HeaderCell>
          <HeaderCell>Type</HeaderCell>
          <HeaderCell>Sluttdato</HeaderCell>
          <HeaderCell>Status</HeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {props.searchState.searchResult?.items?.map((ticketRightHolder) => {
          const hireEndDate = ticketRightHolder.hireEndDate ? toPrintFormat(ticketRightHolder.hireEndDate) : ''
          const isDeleted = ticketRightHolder.isDeleted
          const draftHireEndDate = ticketRightHolder.draftHireEndDate
            ? toPrintFormat(ticketRightHolder.draftHireEndDate)
            : ''
          return (
            <TableRow
              key={ticketRightHolder.id}
              id={`row-${ticketRightHolder.id}`}
              style={{ cursor: 'pointer' }}
              hover
              onClick={() => navigateTo(ticketRightHolder.id, props.searchState.searchResult.items)}
            >
              <DataCell>
                {ticketRightHolder.firstName}
                {ticketRightHolder.surname.trim().length !== 0 && ` ${ticketRightHolder.surname}`}
              </DataCell>
              <DataCell>{toPrintFormat(ticketRightHolder.dateOfBirth)}</DataCell>
              <DataCell>{ticketRightHolder.companyName}</DataCell>
              <DataCell>{ticketRightHolder.employeeNo}</DataCell>
              <DataCell>{ticketRightHolder.isEmployee ? 'Ansatt' : 'Familie'}</DataCell>
              <DataCell>
                {ticketRightHolder.draftHireEndDate !== '' ? (
                  <DraftValue currentValue={hireEndDate} draftValue={draftHireEndDate} hideToolTip />
                ) : (
                  hireEndDate
                )}
              </DataCell>
              <DataCell>
                {isDeleted ? (
                  <BulletBadge variant="danger">Slettet</BulletBadge>
                ) : (
                  <BulletBadge variant="success">Aktiv</BulletBadge>
                )}
              </DataCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

type SearchResultsProps = {
  searchState: SearchHistory
  onPageChange: (requestedPage: number) => void
  onResultsPerPageChange: (resultsPerPage: number) => void
  downloadFile: (format: string) => void
  response: Fetch<Page<TicketRightHolder>>
  downloadResponse: Fetch<undefined>
}
const SearchResult: React.FC<SearchResultsProps> = (props) => {
  const emptyQuery = props.searchState.query === ''

  return (
    <div className="search-result">
      {(props.response.status === FetchState.NOT_STARTED || emptyQuery) && (
        <NoResult
          className="search-result__no_results"
          imgSrc={noResultImg}
          imageHeight="200px"
          text="Når du fyller ut søket over vil resultatene dukke opp her."
        />
      )}
      {props.response.status === FetchState.LOADING && <SearchResultLoader />}
      {props.response.status === FetchState.SUCCESS && props.response.data.items.length > 0 && !emptyQuery && (
        <>
          <div className="fade-out">
            <SearchResultLoader />
          </div>
          <div className="fade-in">
            <GridContainer>
              <GridItem small={6}>
                <Heading2 className="search-result__header">Søkeresultat</Heading2>
              </GridItem>
              <GridItem small={6}>
                <TertiaryButton id="save-results" onClick={() => props.downloadFile('csv')}>
                  <FileIcon />
                  Lagre søkeresultater
                </TertiaryButton>
                <TertiaryButton id="print-addresses" onClick={() => props.downloadFile('addresses-pdf')}>
                  <FileIcon />
                  Skriv ut adresser
                </TertiaryButton>
              </GridItem>
            </GridContainer>
            {props.downloadResponse.status === FetchState.ERROR && (
              <BannerAlertBox
                variant="error"
                title="Kan ikke lage fil"
                closable={true}
                style={{ marginBottom: space.large, marginTop: space.large }}
              >
                Prøv på nytt eller ta kontakt med Entur support om det vedvarer.
              </BannerAlertBox>
            )}
            <>
              <Pagination
                className="search-result__pagination"
                pageCount={props.response.data.totalPages}
                numberOfResults={props.response.data.totalItems}
                currentPage={props.searchState.page}
                onPageChange={props.onPageChange}
                resultsPerPage={props.searchState.perPage}
                onResultsPerPageChange={props.onResultsPerPageChange}
                showInput
              />
            </>
            <TicketHolderList
              searchState={{
                ...props.searchState,
                searchResult: props.response.data,
              }}
            />
          </div>
        </>
      )}
      {props.response.status === FetchState.SUCCESS && props.response.data.items.length === 0 && !emptyQuery && (
        <NoResult
          className="search-result__no_results fade-in"
          imgSrc={emptyResultImg}
          imageHeight="200px"
          text="Søket ditt ga dessverre ingen resultater..."
        />
      )}
      {props.response.status === FetchState.ERROR && (
        <BannerAlertBox
          variant="error"
          title="Søket feilet"
          closable={false}
          style={{ marginBottom: '1em' }}
          className="fade-in"
        >
          Det har skjedd en teknisk feil, ta kontakt med Entur support
        </BannerAlertBox>
      )}
    </div>
  )
}

export default SearchResult
